<script>
	// SVG chart body. The parent owns geometry and interaction state.
	import Axis from "./Axis.svelte";
	import Lines from "./marks/Lines.svelte";
	import Bars from "./marks/Bars.svelte";
	import Pie from "./marks/Pie.svelte";
	import Crosshair from "./Crosshair.svelte";
	import Annotations from "./Annotations.svelte";

	export let type = "line";
	export let spec;
	export let width = 640;
	export let height = 360;
	export let hover = null;
	export let alt = "";
	export let interactive = false;
	export let onHover = null;
	// Geometry is shared with the parent's legend and tooltip, including during SSR.
	export let layout;

	$: pie = type === "pie" || type === "donut";

	let svg;

	function pointerIndex(e) {
		const rect = svg.getBoundingClientRect();
		if (!rect.width || !rect.height) return -1;
		//	the svg scales with preserveAspectRatio=meet: map client px back to viewBox units
		const scale = Math.min(rect.width / width, rect.height / height);
		const ox = (rect.width - width * scale) / 2;
		const oy = (rect.height - height * scale) / 2;
		const px = (e.clientX - rect.left - ox) / scale;
		const py = (e.clientY - rect.top - oy) / scale;
		return layout.hitIndex(px, py);
	}

	function move(e) {
		if (!onHover || pie) return;
		const i = pointerIndex(e);
		if (i >= 0) onHover({ kind: "x", index: i }, e.pointerType === "touch" ? "touch" : "pointer");
	}

	function leave(e) {
		//	touch keeps the last readout until a tap elsewhere (the parent listens on document)
		if (onHover && e.pointerType !== "touch" && !e.relatedTarget?.closest?.(".tooltip")) onHover(null);
	}

	function sliceEnter(i, e) {
		if (onHover) onHover({ kind: "mark", index: i }, e?.pointerType === "touch" ? "touch" : "pointer");
	}

	function sliceLeave(e) {
		if (onHover && e?.pointerType !== "touch") onHover(null);
	}

	function keydown(e) {
		if (!onHover) return;
		const count = pie ? layout.slices.length : spec.x.length;
		if (!count) return;
		const current = hover ? (pie ? layout.slices.findIndex((s) => s.i === hover.index) : hover.index) : -1;
		let next = null;
		switch (e.key) {
			case "ArrowRight":
			case "ArrowDown":
				next = current < 0 ? 0 : Math.min(count - 1, current + 1);
				break;
			case "ArrowLeft":
			case "ArrowUp":
				next = current < 0 ? count - 1 : Math.max(0, current - 1);
				break;
			case "Home":
				next = 0;
				break;
			case "End":
				next = count - 1;
				break;
			case "Escape":
				onHover(null, "keyboard");
				e.preventDefault();
				return;
			default:
				return;
		}
		e.preventDefault();
		if (pie) onHover({ kind: "mark", index: layout.slices[next].i }, "keyboard");
		else onHover({ kind: "x", index: next }, "keyboard");
	}
</script>

<!-- svelte-ignore a11y_no_noninteractive_element_interactions a11y_no_noninteractive_tabindex (One keyboard stop explores data; the adjacent table provides native semantics.) -->
<svg
	bind:this={svg}
	class="chart-svg"
	viewBox="0 0 {width} {height}"
	width="100%"
	height="100%"
	preserveAspectRatio="xMidYMid meet"
	role="img"
	aria-label={alt}
	tabindex={interactive ? 0 : undefined}
	on:keydown={keydown}
>
	<desc>{interactive ? "Folosește săgețile pentru valori, Home și End pentru capete, Escape pentru închidere." : ""} Datele complete sunt disponibile în tabel.</desc>
	{#if pie}
		<Pie {layout} {hover} {interactive} onEnter={sliceEnter} onLeave={sliceLeave} />
	{:else}
		<Axis {layout} />
		<!-- A nested viewport clips marks without document-global IDs, including in PNG exports. -->
		<svg class="plot-marks" x={layout.plot.x - (layout.horizontal ? 0 : 5)} y={layout.plot.y - (layout.horizontal ? 5 : 0)} width={layout.plot.w + (layout.horizontal ? 0 : 10)} height={layout.plot.h + (layout.horizontal ? 10 : 0)} viewBox="{layout.plot.x - (layout.horizontal ? 0 : 5)} {layout.plot.y - (layout.horizontal ? 5 : 0)} {layout.plot.w + (layout.horizontal ? 0 : 10)} {layout.plot.h + (layout.horizontal ? 10 : 0)}" overflow="hidden">
		{#if layout.isLine}
			<Lines {layout} {hover} stacked={layout.stacked} />
		{:else}
			<Bars {layout} {hover} />
		{/if}
		{#if hover && hover.kind === "x"}
			<Crosshair {layout} index={hover.index} />
		{/if}
		</svg>
		<Annotations {layout} />
		<g class="values">
			{#each layout.valueLabels as v}
				<text x={v.x} y={v.y} text-anchor={v.anchor} dominant-baseline={v.baseline}>{v.text}</text>
			{/each}
		</g>
		{#if interactive}
			<!-- svelte-ignore a11y_no_static_element_interactions (Pointer target for the keyboard-accessible parent SVG.) -->
			<rect
				class="hit"
				data-export="skip"
				x={layout.plot.x}
				y={layout.plot.y}
				width={layout.plot.w}
				height={layout.plot.h}
				fill="transparent"
				on:pointermove={move}
				on:pointerdown={move}
				on:pointerleave={leave}
			/>
		{/if}
	{/if}
</svg>

<style>
	.chart-svg {
		display: block;
		width: 100%;
		height: 100%;
		overflow: visible;
		/* article body text is 18px; chart text never scales with it */
		font-size: 12px;
		font-family: var(--_font);
		fill: var(--_ink);
		touch-action: pan-y;
		-webkit-tap-highlight-color: transparent;
	}

	.chart-svg:focus {
		outline: none;
	}

	.chart-svg:focus-visible {
		outline: 2px solid var(--_accent);
		outline-offset: 2px;
		border-radius: 4px;
	}

	.values text {
		fill: var(--_ink);
		font-size: 12px;
		font-weight: 600;
		font-variant-numeric: tabular-nums;
		font-family: var(--_num-font);
		paint-order: stroke;
		stroke: var(--_surface);
		stroke-width: 3px;
		stroke-linejoin: round;
	}

	.hit {
		cursor: crosshair;
	}
</style>
